<template>
    <div>
        <slot />
        <div v-if="!showAgentForm" class="mt-20">
            <g-button shape="round" data-testid="show-form-button" theme="live" @click="showAgentForm = true">
                <g-typography font-weight="semibold">+ Add Agent</g-typography>
            </g-button>
        </div>
        <div v-else>
            <g-form>
                <g-form-item label="Email">
                    <g-input v-model="email" class="input" />
                </g-form-item>
                <g-button
                    :disabled="loading"
                    shape="round"
                    data-testid="update-button"
                    theme="live"
                    @click="createAgent"
                >
                    <g-typography font-weight="semibold">Save</g-typography>
                </g-button>
            </g-form>
            <g-typography font-weight="semibold" tag="p" color="grey-6" class="mt-20">
                Settings for your agent
            </g-typography>
            <g-row type="flex" justify="start" align="middle" class="mt-20 mb-20 agent-info-row">
                <g-switch
                    v-model="agentAcceptsPayments"
                    :loading="loading"
                    :disabled="!storedEmail"
                    @change="handleToggleChange"
                >
                    <g-icon slot="checkedChildren" name="check" size="xsmall" />
                    <g-icon slot="unCheckedChildren" name="times" size="xsmall" />
                </g-switch>
                <div class="toggle-section">
                    <g-typography class="ml-15"> Allow my agent to collect brand payments on my behalf </g-typography>
                    <g-tag
                        v-if="agentAcceptsPayments && agentPaymentSetupStatus === 'active'"
                        color="green"
                        class="ml-15 mt-5"
                    >
                        Agent payment set-up complete
                    </g-tag>
                    <div v-if="agentAcceptsPayments && agentPaymentSetupStatus !== 'active'">
                        <g-tag color="#FFEFDC80" class="ml-15 mt-5 mr-0 payment-tag">
                            <g-button type="link" class="payment-tag-link" @click="showAgentPaymentTooltip = true">
                                Pending agent payment set-up
                            </g-button>
                        </g-tag>
                        <g-button
                            type="text"
                            icon="info-circle"
                            class="payment-setup-status-tooltip"
                            style="border: none; color: #cf4d34"
                            @click="showAgentPaymentTooltip = true"
                        />
                    </div>
                    <g-button type="link" display="block" @click="showTooltip = true"> What does this mean? </g-button>
                </div>
            </g-row>
        </div>
        <agent-tooltip-modal
            :visible="showTooltip"
            :loading="loading"
            tooltip="When enabled, we will notify your agent to set up their agency payment information. When set-up, all payments from brands will go to your agent instead of your personal PayPal account."
            @modal-close="closeAllTooltips"
            @modal-confirm="closeAllTooltips"
        />
        <agent-tooltip-modal
            :visible="showAgentPaymentTooltip"
            :loading="false"
            tooltip="Your agent can finish payment set-up from their Live URL management site."
            @modal-close="closeAllTooltips"
            @modal-confirm="closeAllTooltips"
        />
    </div>
</template>

<script>
    import { useCreatorStore } from '../../../../../stores';
    import { ref, watch } from 'vue-demi';
    import { message } from '@grininc/grin-ui';
    import AgentTooltipModal from './AgentTooltipModal.vue';
    import useEventTracking from '../../../../composables/use-event-tracking';
    import {
        EVENT_NAME_CREATOR_ADD_AGENT_CLICKED,
        EVENT_NAME_CREATOR_DESIGNATE_AGENT_TO_RECEIVE_PAYMENT, EVENT_NAME_CREATOR_REMOVE_AGENT_FROM_RECEIVING_PAYMENT
    } from '../../../../../app-v2/constants/analytics-events';

    export default {
        /**
         * The component name
         *
         * @type {string}
         */
        name: 'AgentInfo',
        components: { AgentTooltipModal },

        setup() {
            const creatorStore = useCreatorStore();
            const email = ref(creatorStore.data.agent?.email);
            const storedEmail = ref(creatorStore.data.agent?.email);
            const showAgentForm = ref(!!creatorStore.data.agent?.email);
            const agentAcceptsPayments = ref(!!creatorStore.data.agent?.isPaymentAgent);
            const agentPaymentSetupStatus = ref(creatorStore.data.agent?.paymentSetupStatus);
            const loading = ref(false);
            const showTooltip = ref(false);
            const showAgentPaymentTooltip = ref(false);

            const toggleLoading = (status) => {
                loading.value = status;
                creatorStore.loadingAgent = status;
            };

            const closeAllTooltips = () => {
                showTooltip.value = false;
                showAgentPaymentTooltip.value = false;
            };

            const handleToggleChange = async () => {
                if (agentAcceptsPayments.value && !storedEmail.value?.trim()) {
                    agentAcceptsPayments.value = false;
                    message.error('Please enter a valid email address to enable payment collection.');
                    return;
                }

                toggleLoading(true);
                if (agentAcceptsPayments.value) {
                    showTooltip.value = true;
                }

                await creatorStore.setPaymentAgent({ email: storedEmail.value });

                if (!creatorStore.error) {
                    const msg = agentAcceptsPayments.value
                        ? 'Agent now receives payments on your behalf.'
                        : 'You have disabled payment collection by your agent.';
                    message.success(msg);
                    const eventName = agentAcceptsPayments.value
                        ? EVENT_NAME_CREATOR_DESIGNATE_AGENT_TO_RECEIVE_PAYMENT
                        : EVENT_NAME_CREATOR_REMOVE_AGENT_FROM_RECEIVING_PAYMENT;
                    useEventTracking().trackEvent(eventName, {
                        creator_id: creatorStore.data.creatorId,
                        agent_id: creatorStore.data.agent?.id
                    });
                } else {
                    message.error(creatorStore.error);
                    agentAcceptsPayments.value = false;
                }
                email.value = storedEmail.value;
                toggleLoading(false);
            };

            const createAgent = async () => {
                useEventTracking().trackEvent(EVENT_NAME_CREATOR_ADD_AGENT_CLICKED, {
                    creator_id: creatorStore.data.creatorId,
                    agent_id: creatorStore.data.agent?.id
                });

                if (!email.value?.trim()) {
                    message.error('Please enter a valid email address.');
                    email.value = storedEmail.value;
                    return;
                }

                toggleLoading(true);

                await creatorStore.linkAgent({ email: email.value });

                if (!creatorStore.error) {
                    message.success('Agent has been added.');
                    showAgentForm.value = true;
                } else {
                    message.error(creatorStore.error);
                    email.value = storedEmail.value;
                }

                toggleLoading(false);
            };

            watch(
                [
                    () => creatorStore.data.agent?.email,
                    () => creatorStore.data.agent?.isPaymentAgent,
                    () => creatorStore.data.agent?.paymentSetupStatus
                ],
                ([newEmail, newIsPaymentAgent, newPaymentStatus]) => {
                    storedEmail.value = newEmail;
                    agentAcceptsPayments.value = newIsPaymentAgent;
                    agentPaymentSetupStatus.value = newPaymentStatus;
                }
            );

            return {
                createAgent,
                showAgentForm,
                loading,
                email,
                storedEmail,
                agentAcceptsPayments,
                showTooltip,
                showAgentPaymentTooltip,
                closeAllTooltips,
                handleToggleChange,
                agentPaymentSetupStatus
            };
        },
        mounted() {
            useCreatorStore().getAgent();
        }
    };
</script>

<style lang="scss" scoped>
    .agent-info-row {
        min-width: 272px;
        flex-wrap: nowrap;
        .toggle-section {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        }
        .payment-tag {
            color: $g-color-orange-10;
            border-color: $g-color-orange-11;
        }
        .payment-tag-link {
            color: $g-color-orange-10;
            height: 24px;
            padding: 0;
        }
        .payment-setup-status-tooltip {
            border: none;
            color: $g-color-orange-10;
        }
    }
</style>
